import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { t } from 'i18next';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { useNavigate } from 'react-router-dom';
import siteSetup from 'src/js/modules/site-setup';
import trackers from 'src/js/modules/analytics/segment/trackers';
import { SiteSetupContext } from 'src/js/modules/site-setup/helpers/site-setup-context';
import Layout from 'src/js/components/layout';
import fetchTemplatesData from 'src/js/modules/templates/thunks/fetch-templates-data';
import getSiteIdToRecreate from 'src/js/modules/sites/selectors/get-site-id-recreate';
import getSiteIdToSwitch from 'src/js/modules/sites/selectors/site-id-to-switch-template';
import getLocale from 'src/js/modules/i18n/selectors/locale';
import HeaderGalleryPageContainer from 'src/js/containers/header-gallery-page-container';
import SiteSetupLayout from 'src/js/components/site-setup/site-setup-layout';
import routesMap from '../../router/helpers/routes-map';
import getSuggestions from '../../helpers/get-suggestions';
import useFeatureFlags from '../../modules/feature-flags/hooks/use-feature-flags';
import BusinessCategoryFieldContainer from '../business-category-field-container';
import {
  AI_OPTIONAL_TEMPLATE_SELECTION_STEP_FLAG,
  ENTER_KEY,
  BUSINESS_CATEGORY_FIELD_NAME,
} from '../../constants/common';

const { constants } = siteSetup;
const { siteSetupNames, common } = constants;
const { trackSiteSetupSkipButtonClicked, trackSitesetupBusinessCategoryProvided } = trackers;
const { Paragraph, Box, Stack } = designSystem;
const { useForm } = reactHookForm;

const getCaptions = () => ({
  title: t('What type of website do you want to create?'),
  buttons: {
    next: t('Continue'),
  },
  error: {
    required: t('This field is required'),
  },
});

function CategoryStepContainer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const captions = useMemo(() => getCaptions(), []);
  const suggestions = useMemo(() => getSuggestions(), []);
  const context = useContext(SiteSetupContext);
  const stepId = siteSetupNames.CATEGORY;
  const { updateData, getData, isCategoryLoaded } = context;
  const contextData = getData();
  const [selectedOption, setSelectedOption] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(!selectedOption);
  const siteIdToRecreate = useSelector(getSiteIdToRecreate);
  const locale = useSelector(getLocale);
  const siteIdToSwitch = useSelector(getSiteIdToSwitch);
  const siteId = siteIdToRecreate || siteIdToSwitch || null;

  // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  const { number: currentStep } = useMemo(siteSetup.helpers.getCurrentStep, []);
  const { control, handleSubmit } = useForm({
    defaultValues: {
      [BUSINESS_CATEGORY_FIELD_NAME]: contextData.businessCategory || '',
    },
    mode: 'all',
  });

  const [featureFlags] = useFeatureFlags([AI_OPTIONAL_TEMPLATE_SELECTION_STEP_FLAG]);
  const { [AI_OPTIONAL_TEMPLATE_SELECTION_STEP_FLAG]: isOptionalTemplateSelectionStepEnabled } =
    featureFlags;

  const onSkip = useCallback(() => {
    updateData({
      archetype: null,
      businessCategory: null,
      businessCategorySlug: null,
      templates: [],
    });
    trackSiteSetupSkipButtonClicked({
      stepId,
      archetypeId: null,
      businessCategory: null,
    });

    if (isOptionalTemplateSelectionStepEnabled) {
      navigate(routesMap.howToBuild.url());
    } else {
      navigate(routesMap.gallery.url());
    }
  }, [updateData, stepId, isOptionalTemplateSelectionStepEnabled, navigate]);

  const onNext = useCallback(() => {
    setIsLoading(true);

    handleSubmit(async () => {
      const selectedArchetype = selectedOption?.archetype || contextData.archetype;

      if (selectedOption) {
        updateData({
          businessCategory: selectedOption.label,
          businessCategorySlug: selectedOption.value,
          archetype: selectedArchetype,
          templates: selectedOption.templates || [],
        });

        await dispatch(
          fetchTemplatesData({
            pageSize: common.TEMPLATES_PAGE_SIZE,
            business_category: selectedOption.value || null,
            locale,
          })
        );
        setIsLoading(false);
      }

      trackSitesetupBusinessCategoryProvided({
        siteId,
        stepId,
        businessCategory: selectedOption?.value || contextData.businessCategorySlug,
        archetypeId: selectedArchetype || null,
      });

      if (contextData.useTemplateFlow) {
        navigate(routesMap.siteName.url());
      } else if (isOptionalTemplateSelectionStepEnabled) {
        navigate(routesMap.howToBuild.url());
      } else {
        navigate(routesMap.gallery.url());
      }
    })();
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [
    selectedOption,
    contextData.businessCategorySlug,
    isOptionalTemplateSelectionStepEnabled,
    navigate,
  ]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === ENTER_KEY && !isNextDisabled) {
        onNext();
      }
    },
    [isNextDisabled, onNext]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    if (isCategoryLoaded && contextData.businessCategory) {
      setSelectedOption({
        label: contextData.businessCategory,
        value: contextData.businessCategorySlug,
        archetype: contextData.archetype,
        templates: contextData.templates || [],
      });
      setIsNextDisabled(false);
    }
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, [isCategoryLoaded]);

  if (!isCategoryLoaded) {
    return null;
  }

  return (
    <Layout Header={HeaderGalleryPageContainer} headerOnSkip={onSkip}>
      <SiteSetupLayout
        className="ws-site-setup-layout-business-category"
        captions={captions}
        currentStep={currentStep}
        next={{
          callback: onNext,
          disabled: isNextDisabled,
        }}
        isLoading={isLoading || !isCategoryLoaded}
        areButtonsOnSeparateRow={false}
      >
        <div className="ws-site-setup-layout-business-category__content">
          <BusinessCategoryFieldContainer
            control={control}
            stepId={stepId}
            openMenu={isNextDisabled}
            validate={(value, businessCategoryValue) => {
              if (value && selectedOption && businessCategoryValue === selectedOption.label) {
                setIsNextDisabled(false);
              } else if (!isNextDisabled) {
                setIsNextDisabled(true);
              }

              return true;
            }}
            onChange={(option) => {
              setSelectedOption(option);
              setIsNextDisabled(false);
            }}
            onInput={() => setIsNextDisabled(true)}
          />
          <Box marginTop="spacing-m">
            <Paragraph size="small" appearance="medium-emphasis">
              {t('Examples:')}
            </Paragraph>
            <Box marginTop="spacing-3-xs">
              <Stack gap="spacing-2-xs">
                {suggestions.map(({ key, label }) => (
                  <Paragraph key={key} size="large">
                    {label}
                  </Paragraph>
                ))}
              </Stack>
            </Box>
          </Box>
        </div>
      </SiteSetupLayout>
    </Layout>
  );
}

export default CategoryStepContainer;
